import { RoadmapItem } from './get-started-page/gs-roadmap-card/gs-roadmap-card';
import { environment } from '../environments/environment';

const EMAIL = environment.DATANEX_SUPPORT_EMAIL;

export interface FaqItem {
  q: string;
  a: string;
}

export interface FaqSection {
  id: string;
  label: string;
  items: FaqItem[];
}

export const FAQ_SECTIONS: FaqSection[] = [
  {
    id: 'getting-started',
    label: 'Getting Started',
    items: [
      {
        q: 'What is DataNex+?',
        a: "DataNex+, the expansion of DataNex, is a foundational intelligence platform built for ADB's work. It helps you unlock the full value of the bank's assets by bringing together trusted and governed datasets, AI capabilities, and other reusable building blocks into a single platform. Whether you're exploring a new idea, addressing a business need, or looking for ways to strengthen a project, DataNex+ provides a starting point for discovering what capabilities already exist across ADB before building something new.\n\nSimply describe what you are looking for, and the system connects you to available resources that you can reuse instead of starting from scratch. By making ADB's capabilities easier to find, access, and reuse, DataNex+ helps turn available information and technology investments into smarter decisions, stronger operations, and greater development impact. DataNex+ will grow with ADB: it will continue to expand its capabilities, deepen its understanding of ADB's context, and integrate more seamlessly into the way ADB works.",
      },
      {
        q: 'How can DataNex+ help me in my work?',
        a: "DataNex+ can help you to unlock institutional knowledge, achieve impact faster and make better informed decisions by supporting you across three pillars:\n\n• Discover. DataNex+ brings together trusted data, knowledge, and digital assets into a single, intuitive platform.\n• Connect. DataNex+ helps you identify relevant resources and connect with the people behind them, enabling greater collaboration across teams and departments. It also allows you to contribute assets for colleagues' use.\n• Reuse. DataNex+ makes it easier for you to find and build on available datasets, tools, and solutions instead of reinventing them so you can work more efficiently and maximize the value of ADB's collective investments.",
      },
      {
        q: 'How does DataNex+ differ from other data platforms within ADB?',
        a: "DataNex+ complements ADB's existing platforms by providing a contextual discovery experience. It also serves as a centralized source of relevant datasets from outside ADB—such as public, partner, and commercial sources—to make them easier for personnel to access. Beyond datasets, DataNex+ brings together other assets from various domains into one platform, making it easier to identify what already exists within ADB that may be leveraged to address a specific business need.",
      },
    ],
  },
  {
    id: 'accessing-datanex',
    label: 'Accessing DataNex+',
    items: [
      {
        q: 'Do I have access to DataNex+?',
        a: "DataNex+ is meant to be available to all personnel with an ADB email address. Beginning in October, early release users will be able to sign in using ADB's Single Sign-On (SSO). Access will be expanded over time as the platform continues to evolve.",
      },
      {
        q: 'How can I access DataNex+?',
        a: 'You can access the platform at datanex.adb.org.',
      },
    ],
  },
  {
    id: 'understanding-assets',
    label: 'Understanding Assets',
    items: [
      {
        q: 'What are assets?',
        a: "In DataNex+, an asset is any discoverable and reusable resource available through the platform. Assets can include datasets, models, dashboards, workflows, templates, AI agents, pipelines, and other resources that users can access and build upon.\n\nEach asset is accompanied by metadata that provides important information such as ownership, life cycle and trust status, classification, and access requirements. These attributes help users understand an asset's purpose, reliability, and availability, and determine how the asset can be accessed and reused.\n\nAvailability and reuse permissions vary depending on an asset's classification, ownership, and access conditions.",
      },
      {
        q: 'What assets can I find on DataNex+?',
        a: 'These assets are available on DataNex+ in October 2026:\n\n• Datasets\n• AI Tools\n• AI Agents\n• AI Products',
      },
      {
        q: 'How do AI tools, AI agents, and AI products differ from each other?',
        a: 'In DataNex+, AI assets are grouped into three categories:\n\n• AI Tool: A governed AI capability that performs a common task and can be used across many scenarios. Examples include translation, image generation, and summarization.\n• AI Agent: A governed AI capability designed for a specific use case. It leverages Model Context Protocol (MCP) capabilities to access relevant context, information, and resources needed to perform a defined task.\n• AI Product: A complete, end-to-end solution that brings together AI capabilities, data, workflows, and user interfaces to deliver a specific user experience or business outcome.',
      },
      {
        q: 'Which AI asset category should I use?',
        a: "The right AI asset category depends on your needs. If you need support for a common task, an AI tool may be sufficient. If you need AI tailored to a specific use case, an AI agent may be more appropriate. If you're looking for a complete user-facing solution, an AI product is likely the best fit.",
      },
      {
        q: 'Will more asset types be added in the future?',
        a: "DataNex+ is designed to grow alongside ADB's data and AI ecosystem. New asset types, integrations, and features may be added over time to improve discovery, reuse, and collaboration.",
      },
      {
        q: "Are all of ADB's assets available in DataNex+?",
        a: `Not all of ADB's assets are available in DataNex+. That said, the catalog will grow over time to accommodate new assets and asset types. Reach out to us at ${EMAIL} if you're interested in submitting assets.`,
      },
      {
        q: 'How will I know if new data assets and asset types are added?',
        a: 'Our dedicated "What\'s New" section tracks what new assets and asset types are added.',
      },
    ],
  },
  {
    id: 'finding-accessing',
    label: 'Finding and Accessing Assets',
    items: [
      {
        q: 'I can see a section on recommended assets upon logging in. How does DataNex+ determine what assets to recommend to me?',
        a: 'DataNex+ recommends assets based on the department that you are part of.',
      },
      {
        q: 'How does DataNex+ match me to assets relevant to my business need?',
        a: "DataNex+'s ADB Contextual Search Agent enables you to search in natural language. The system will interpret your intent to recommend relevant assets and AI capabilities from across DataNex+. This helps you discover resources relevant to your business need, even when you are not sure exactly what you are looking for.",
      },
      {
        q: 'Can I explore DataNex+ assets that were developed outside of my department?',
        a: "Yes. DataNex+ is designed to help users discover cataloged assets across organizational and domain boundaries, making it easier to find and reuse resources developed by other departments and teams. However, while discoverability of an asset is broad, its access and reuse depend on the asset's governance, security, confidentiality, licensing, and other applicable restrictions. Each asset's metadata includes these details, as well as information on the asset's trust status and life cycle stage.",
      },
      {
        q: 'Can I automatically access all assets on DataNex+?',
        a: 'No. Access will depend on the access permissions per asset (i.e., public, internal ADB, restricted). Some assets may be accessible immediately, while others may require approval from the product owners. DataNex+ respects those controls and helps users request access when needed, but approval will depend on asset owners.',
      },
      {
        q: 'Can I automatically reuse any of the assets on DataNex+?',
        a: 'No. Access will depend on the access permissions per asset (i.e., public, internal ADB, restricted). Some assets may be accessible immediately, while others may require approval from the product owners. DataNex+ respects those controls and helps users request access when needed, but approval will depend on asset owners.',
      },
      {
        q: "What if I can't find what I'm looking for?",
        a: 'You can try refining your prompts or keywords if your initial search does not yield results. If you still cannot find what you need after refining your search, please send us a message via the Need Help page. Your feedback, including suggestions on assets to expand the catalog, will help inform future enhancements.',
      },
    ],
  },
  {
    id: 'collaboration',
    label: 'Collaboration',
    items: [
      {
        q: 'Can I collaborate with teammates on DataNex+?',
        a: "Yes, you can invite your teammates to the workspace that you're working on.",
      },
      {
        q: 'Can I share DataNex+ assets with colleagues?',
        a: 'Yes, DataNex+ has a share button to make it easy for users to share assets with colleagues, provided that they also have DataNex+ access.',
      },
    ],
  },
  {
    id: 'contributing',
    label: 'Contributing Assets',
    items: [
      {
        q: 'I want to contribute an asset. How do I go about it?',
        a: `You can reach out to us at ${EMAIL} or click on the "Contribute an Asset" button in DataNex+ if you want to contribute an asset. In the future, we will integrate a feature that will enable users to submit an asset on the platform itself.\n\nBefore you submit an asset:\n• confirm you have sharing rights\n• identify access restrictions`,
      },
      {
        q: 'What criteria are used to evaluate whether assets can be added to DataNex+?',
        a: "An asset may be considered for inclusion in DataNex+ when it:\n\n• is relevant to ADB's work and has potential for reuse;\n• has an identified business owner and contact;\n• has the required descriptive and technical metadata;\n• has its ADB information classification and access conditions confirmed by the owning business unit;\n• has its source, licensing, and permitted-use conditions documented;\n• meets applicable quality, security, privacy, governance and Responsible AI requirements; and\n• has a defined arrangement for maintenance, updates or life cycle management.\n\nInclusion in the catalog makes an asset discoverable. It does not automatically mean that the asset is approved for all use cases, available to every user, or independently verified by DataNex+.",
      },
      {
        q: 'Can I submit what I worked on in Spaces as a new asset?',
        a: 'Yes, but its approval is not automatic. It will have to be reviewed and vetted by the teams responsible for the assets. In addition, the DataNex+ also performs catalog-onboarding checks.',
      },
      {
        q: 'Will assets that I submit automatically show up in DataNex+?',
        a: 'No. Submitted assets will need to be reviewed and approved by teams responsible for the assets.',
      },
    ],
  },
  {
    id: 'help',
    label: 'Help',
    items: [
      {
        q: 'I found an error or issue while using DataNex+. How do I give feedback?',
        a: 'We welcome feedback to help us improve DataNex+. If you see an error in the chat feature, we invite you to use the thumbs up/thumbs down feature and provide the details when prompted. If the error or issue is found elsewhere in DataNex+, please share the details by filling up the feedback form available in the "Need Help?" tab.',
      },
      {
        q: 'My question is not listed here.',
        a: `You can reach out to us by contacting us at ${EMAIL} or filling up the feedback form available in the "Need Help?" tab.`,
      },
    ],
  },
];

export const FOOTER_LINKS: { label: string; href?: string }[] = [
  { label: 'ADB Contacts', href: 'https://www.adb.org/contacts/main' },
  { label: 'Access to Information', href: 'https://www.adb.org/who-we-are/access-information' },
  { label: 'Terms of Use', href: 'https://www.adb.org/terms-use' },
];

export const CONTRIBUTE_EMAIL = `${EMAIL}`;
export const CONTRIBUTE_SUBJECT = 'Asset Contribution';
export const CONTRIBUTE_BODY = {
  intro: 'I have an asset that I would like to contribute to DataNex+.',
  boldFields: [
    ['Asset name', '[Enter asset name]'],
    ['Asset type', '[Select one: Dataset / AI Tool / AI Agent / Platform / Other]'],
    ['Short description', '[Briefly describe the asset]'],
  ] as [string, string][],
  italicNote:
    "Note: This is a direct feedback channel to the DataNex+ product team and is not ADB's formal IT ticketing system.",
};

export const DATANEX_TRAILER_URL =
  'https://dev.datanex.adb.org/marketing/videos/DataNexplustrailer.mp4';

export const WN_TABS = [
  { id: 'discover', label: 'Discover' },
  { id: 'connect', label: 'Connect' },
  { id: 'reuse', label: 'Reuse' },
] as const;

export const WN_TAB_CONTENT: Record<string, { accent: string; titleRest: string; body: string[] }> =
  {
    discover: {
      accent: 'Discover',
      titleRest: 'existing capabilities',
      body: [
        'Discover assets across ADB and connect with the people behind them. DataNex+ brings data, AI tools, and other assets into one place, with clear ownership so you can find what already exists and who to engage.',
        'Make better-informed decisions and build on what already exists instead of starting from scratch.',
      ],
    },
    connect: {
      accent: 'Connect',
      titleRest: 'people, data and ways of working',
      body: [
        'Connect people around the data, assets, and repeatable approaches that support their work. DataNex+ makes it easier to share knowledge, collaborate through Workspaces, and carry proven ways of working across teams.',
        'Spend less time rebuilding context and make successful approaches easier to repeat across ADB.',
      ],
    },
    reuse: {
      accent: 'Reuse',
      titleRest: 'and contribute back',
      body: [
        'Turn data held across ADB into shared, reusable value. By giving people a unified place to contribute and publish the data they hold, DataNex+ makes institutional knowledge available beyond the teams that created it.',
        'Reduce duplication and unlock greater value from data already held across ADB.',
      ],
    },
  };

export const WN_ROADMAP_ITEMS: RoadmapItem[] = [
  {
    quarter: 'Milestone 1',
    title: 'Richer Assets, Effortless Discovery',
    desc: 'Expand the catalog with richer data products and additional reusable capabilities such as eligible models and dashboards.\n\nDeeper ADB context, semantic retrieval, and richer filters make discovery more relevant, while supported assets can increasingly be explored or interacted with directly within DataNex+ before users decide how to access or reuse them.',
  },
  {
    quarter: 'Milestone 2',
    title: 'From Discovery to Action',
    desc: "Move beyond finding assets to reusing them in ADB's work via APIs, connectors, and approved integration patterns. Use Cases show how ADB has applied capabilities before, while curated Collections feature assets around common needs.\n\nAsset owners can also contribute reusable capabilities through a streamlined onboarding process with governance, access, and Responsible AI embedded into the workflow.",
  },
  {
    quarter: 'Milestone 3',
    title: 'Build Smarter, Orchestrate with Trust',
    desc: 'Build new solutions and workflows by combining trusted datasets, models, agents, tools, and other reusable capabilities already available across ADB.\n\nDataNex+ evolves into a governed orchestration environment for multi-model and agentic workflows, with human oversight, governance standards, and Responsible AI applied throughout execution.',
  },
];
